import type { Page } from 'playwright';
import type { AutonomousCursor } from '../autonomous-cursor.js';
import type { BrowserLogger } from '../logger.js';
import { applyHumanClickDelay } from '../click-timing.js';
import { createRunCodeCueHook, type CueBudget } from './run-code-cue.js';
import {
  asEmitter,
  buildListenerStripWarning,
  detectFunctionExpressionBody,
  guardRunCodeFetch,
  guardRunCodePageRequest,
  guardRunCodeWaits,
  stripNewListeners,
  type RunCodeEgressPolicy,
  type RunCodeFetch,
  type RunCodeWaitGuardCounts,
  type RunCodeWaitGuardTarget,
} from './run-code-guard.js';

/** Structured outcome of {@link executeRunCode}. Never thrown — always returned. */
export interface RunCodeOutcome {
  /** True when the code ran to completion without throwing or being aborted. */
  ok: boolean;
  /** Raw return value of the code (only when `ok`). */
  result?: unknown;
  /** Error message when the code threw (not when aborted). */
  error?: string;
  /** True when the run was aborted via the signal. */
  aborted: boolean;
  /** Listener-strip warning text, if the code attached stray listeners. */
  warning: string;
}

/**
 * Execute driver-side Playwright `code` against `page` with named `bindings`
 * injected as in-scope identifiers. The SINGLE source of truth for the run_code
 * primitive: used both by {@link PlaywrightClient.runCode} (the live fast-drive
 * agent) and by deterministic cache replay of `run_code` actions, so the two can
 * never drift in injection, guarding, or listener cleanup.
 *
 * Guards `fetch` / `page.request` / event waits, races the code against `signal`,
 * and strips any dialog/download/filechooser listeners the code added
 * (API-1W8 / API-1VZ). Bindings are passed by reference as named function params,
 * never concatenated into the code string — a binding value can't inject code.
 * Never throws; returns a {@link RunCodeOutcome}.
 *
 * @internal Code-injection primitive with strong trust assumptions (caller supplies
 * a raw Playwright page + arbitrary code). Exported only for the apps/api cache-replay
 * path; not a stable surface for external consumers.
 */
export async function executeRunCode(opts: {
  /** Opt-in egress policy for model-authored code (see {@link RunCodeEgressPolicy}). */
  egressPolicy?: RunCodeEgressPolicy;
  page: Page;
  code: string;
  bindings?: Record<string, unknown>;
  signal?: AbortSignal;
  logger: Pick<BrowserLogger, 'debug' | 'warn'>;
  /**
   * Fast-drive autonomous cursor. When provided, the in-page cursor runtime is
   * installed and its active flag is flipped ON for the duration of this block
   * (and OFF in `finally`) so run_code interactions — and ONLY run_code, not
   * ref-based actions — animate an on-brand cursor in the recorded video. When the
   * session is actually recording, it additionally cues each interaction BEFORE
   * dispatch (see `run-code-cue.ts`). Wired for both live fast-drive and cache
   * replay (both call this primitive). Best-effort; never throws.
   */
  autonomousCursor?: AutonomousCursor;
  /**
   * Receives the dwell each anticipatory cue spends, so the caller's action
   * deadline can be extended by that much (bounded by the caller's own cap).
   * Without it, cue dwell would be spent inside the deadline that exists to bound
   * the snippet's real work — a snippet could time out purely because it animated.
   */
  cueBudget?: CueBudget;
}): Promise<RunCodeOutcome> {
  const { page, code, bindings, signal, logger, autonomousCursor, cueBudget } = opts;

  // Reject a bare function-expression body BEFORE any side effect (cursor install,
  // listener baselines, page guards). Left to compile, such a body would be created
  // and discarded by the `async () => { … }` wrapper below and report success having
  // executed nothing — see detectFunctionExpressionBody.
  const shapeError = detectFunctionExpressionBody(code);
  if (shapeError !== null) {
    logger.warn('[DirectPlaywright] runCode rejected function-expression body', {
      event: 'runcode_function_expression_rejected',
      codePreview: code.slice(0, 80),
    });
    return { ok: false, error: shapeError, aborted: false, warning: '' };
  }

  // Activate the fast-mode cursor for this block: install (idempotent, page-keyed),
  // flip the active flag on, and open a fresh cue generation so anything a previous
  // block left on screen is dropped. Torn down in `finally`, so only run_code
  // execution animates. Guarded so cursor wiring never breaks the run.
  let cueGeneration: number | null = null;
  try {
    await autonomousCursor?.install(page);
    await autonomousCursor?.setActive(page, true);
    cueGeneration = (await autonomousCursor?.beginBlock(page)) ?? null;
  } catch (err) {
    logger.debug('[DirectPlaywright] runCode cursor activate failed', {
      error: err instanceof Error ? err.message : String(err),
    });
  }

  const baselineDialog = asEmitter(page).rawListeners('dialog');
  const baselineDownload = asEmitter(page).rawListeners('download');
  const baselineFileChooser = asEmitter(page).rawListeners('filechooser');
  const restoreRunCodeWaits = guardRunCodeWaits(page as unknown as RunCodeWaitGuardTarget, logger);

  const runController = new AbortController();
  // Keep cancellation provenance in a closure the evaluated source cannot
  // reach. Reading AbortSignal.prototype.aborted after hostile code ran would
  // let that code forge cancellation by replacing the global getter.
  let aborted = signal?.aborted ?? false;
  const onOuterAbort = () => {
    aborted = true;
    runController.abort();
  };
  if (signal?.aborted) {
    onOuterAbort();
  } else {
    signal?.addEventListener('abort', onOuterAbort, { once: true });
  }

  let result: unknown;
  let threwError: Error | null = null;
  let guardedWaits: RunCodeWaitGuardCounts = { fileChooser: 0, request: 0, response: 0 };
  const guardedFetch = guardRunCodeFetch(logger, opts.egressPolicy);
  // Guard diagnostic request surfaces, then default a human-length press delay
  // onto every click this code issues (defeats same-tick click races in the
  // page under test) and cue each interaction before it is dispatched. This is
  // the fast-mode choke point: live fast-drive AND cache replay both run through
  // here. The cue hook is attached only when the cursor is actually cueing, so a
  // non-recording session pays nothing.
  const beforeAction = autonomousCursor?.cuesEnabled
    ? createRunCodeCueHook({
        page,
        cursor: autonomousCursor,
        signal: runController.signal,
        budget: cueBudget,
      })
    : undefined;
  const guardedPage = applyHumanClickDelay(guardRunCodePageRequest(page, logger, opts.egressPolicy), {
    beforeAction,
  });

  try {
    if (!aborted) {
      // Binding names become `new Function` parameter identifiers — a non-identifier
      // key (space, comma, paren) would throw a SyntaxError and break the
      // never-throw contract. Today's keys are fixed (vars/macro/attach), so this
      // only guards future callers: drop any invalid-named binding rather than
      // crash. Values are passed by reference, never concatenated into the code.
      const bindingNames = (bindings ? Object.keys(bindings) : []).filter((k) => {
        const ok = /^[A-Za-z_$][\w$]*$/.test(k);
        if (!ok)
          logger.warn('[DirectPlaywright] runCode dropping non-identifier binding', { key: k });
        return ok;
      });
      const bindingValues = bindingNames.map((k) => bindings![k]);
      const fn = new Function(
        'page',
        'fetch',
        ...bindingNames,
        `return (async () => { ${code} })()`
      ) as (p: Page, fetch: RunCodeFetch, ...bindings: unknown[]) => Promise<unknown>;

      const abortPromise = new Promise<never>((_, reject) => {
        runController.signal.addEventListener('abort', () => reject(new Error('runCode aborted')), {
          once: true,
        });
      });
      const inner = fn(guardedPage, guardedFetch, ...bindingValues);
      inner.catch((err) => {
        logger.debug('[DirectPlaywright] runCode orphan rejected after race', {
          error: err instanceof Error ? err.message : String(err),
        });
      });

      try {
        result = await Promise.race([inner, abortPromise]);
      } catch (err) {
        if (aborted) {
          // The closure above, not authored error text or mutable signal
          // prototypes, is the cancellation source of truth.
        } else {
          threwError = err instanceof Error ? err : new Error(String(err));
        }
      }
    }
  } catch (err) {
    if (aborted) {
      // See the inner catch: only the outer abort callback sets this flag.
    } else {
      threwError = err instanceof Error ? err : new Error(String(err));
    }
  } finally {
    signal?.removeEventListener('abort', onOuterAbort);
    guardedWaits = restoreRunCodeWaits();
    // Tear the cursor down: drop this generation's transient visuals (halo, ripple,
    // glow, keycaps) and cancel their timers so an aborted block leaves nothing on
    // screen, then deactivate so ref-based actions outside this block keep the
    // driver cursor (no double-render). Best-effort; never block.
    try {
      if (cueGeneration !== null) await autonomousCursor?.endBlock(page, cueGeneration);
      await autonomousCursor?.setActive(page, false);
    } catch {
      /* never block the run on cursor teardown */
    }
  }

  let stripped = 0;
  let strippedDownload = 0;
  let strippedFileChooser = 0;
  try {
    if (!page.isClosed()) {
      stripped = stripNewListeners(page, 'dialog', baselineDialog);
      strippedDownload = stripNewListeners(page, 'download', baselineDownload);
      strippedFileChooser = stripNewListeners(page, 'filechooser', baselineFileChooser);
    }
  } catch (cleanupErr) {
    logger.debug('[DirectPlaywright] runCode listener cleanup failed', {
      error: cleanupErr instanceof Error ? cleanupErr.message : String(cleanupErr),
    });
  }
  const fileChooserWarningCount = Math.max(strippedFileChooser, guardedWaits.fileChooser);
  if (stripped > 0 || strippedDownload > 0 || fileChooserWarningCount > 0) {
    logger.warn('[DirectPlaywright] runCode stripped agent-added listeners', {
      event: 'runcode_listeners_stripped',
      dialog: stripped,
      download: strippedDownload,
      fileChooser: strippedFileChooser,
      fileChooserWaitsGuarded: guardedWaits.fileChooser,
      requestWaitsGuarded: guardedWaits.request,
      responseWaitsGuarded: guardedWaits.response,
      codePreview: code.slice(0, 80),
    });
  }
  const warning = buildListenerStripWarning(stripped, strippedDownload, fileChooserWarningCount);

  return {
    ok: !aborted && threwError === null,
    result: aborted || threwError ? undefined : result,
    error: threwError?.message,
    aborted,
    warning,
  };
}
